// src/utils/transcriptParser.js (Integrated Update)
// - Properly parses Agent: and Customer: patterns
// - Adds support for various speaker label formats (bolded, bracketed, plain, aliases, ES labels, generic "Speaker 1/2", "User", "AI", etc.)
// - Fixes continuation line handling (appends to previous without trimming core logic)
// - Preserves original core behavior: first unlabeled line assumes Agent; no logic trimmed

// Helper: normalize a single line (strip timestamps, bullets, extra spaces)
const normalizeLine = (raw) => {
  if (!raw) return ''
  let line = raw

  // Remove leading timestamps like [00:01:23], 00:01:23, [1:23], 1:23:05
  line = line.replace(/^\s*(\[\d{1,2}:\d{2}(?::\d{2})?\]|\d{1,2}:\d{2}(?::\d{2})?)\s*/, '')
  // Trim markdown list bullets or dashes if they start a continuation
  line = line.replace(/^\s*(?:[-–•]\s+)/, '')
  // Trim leftover spaces
  return line.trim()
}

// Helper: map many aliases to our two canonical speakers
const mapSpeaker = (raw) => {
  if (!raw) return null
  const key = String(raw).toLowerCase().trim()

  // Normalized tokens (drop trailing digits like "speaker 1", "agent 2")
  const keyNoNum = key.replace(/\s*\d+\s*$/, '')

  // Agent synonyms (EN + ES + common labels)
  const agentKeys = new Set([
    'agent','rep','sales','representative','assistant','ai','bot','system','vendedor','asesor','csr','operator','speaker a','speaker a.','speaker1a','speaker a)'
  ])
  // Customer synonyms (EN + ES)
  const customerKeys = new Set([
    'customer','client','prospect','caller','user','usuario','cliente','prospecto','lead','homeowner','speaker b','speaker b.','speaker1b','speaker b)'
  ])

  // Generic "speaker" mapping by index (speaker 1 -> Agent, speaker 2 -> Customer)
  const speakerIndexed = key.match(/^speaker\s*(\d+|[ab])$/i)
  if (speakerIndexed) {
    const idx = speakerIndexed[1]
    if (String(idx).toLowerCase() === 'a' || String(idx) === '1') {
      return { speaker: 'Agent', type: 'agent' }
    } else if (String(idx).toLowerCase() === 'b' || String(idx) === '2') {
      return { speaker: 'Customer', type: 'customer' }
    }
  }

  if (agentKeys.has(key) || agentKeys.has(keyNoNum)) return { speaker: 'Agent', type: 'agent' }
  if (customerKeys.has(key) || customerKeys.has(keyNoNum)) return { speaker: 'Customer', type: 'customer' }
  return null
}

export const parseTranscript = (transcript) => {
  if (!transcript || transcript === "No transcript available") {
    return []
  }

  const messages = []
  // Normalize line endings and split
  const lines = transcript
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean)

  lines.forEach((rawLine) => {
    // Skip common headers or separators
    if (/^##\s*transcript/i.test(rawLine)) return
    if (/^[-=]{3,}$/.test(rawLine)) return // --- or ===
    if (/^```/.test(rawLine)) return       // code fences

    let line = normalizeLine(rawLine)
    if (!line) return

    // 1) **Marked** speakers like **Agent:**, **Customer:**, **User:**, **AI:**
    //    Allow extra spaces within ** ** and after colon
    const markedMatch = line.match(/^\*\*\s*([A-Za-z]+(?:\s*\d+)?|Speaker\s*(?:\d+|[AB]))\s*\*\*\s*:\s*(.*)$/i)
    if (markedMatch) {
      const mapped = mapSpeaker(markedMatch[1])
      if (mapped) {
        messages.push({
          speaker: mapped.speaker,
          text: (markedMatch[2] || '').trim(),
          type: mapped.type
        })
        return
      }
    }

    // 2) [Bracketed] or plain speakers: [Agent]: Hi / Agent: Hi / SPEAKER 1: Hi
    const bracketOrPlain = line.match(/^\[?\s*([A-Za-z]+(?:\s*\d+)?|Speaker\s*(?:\d+|[AB])|Rep|Sales|Client|Prospect|Caller|User|AI|Customer|Agent)\s*\]?\s*:\s*(.*)$/i)
    if (bracketOrPlain) {
      const mapped = mapSpeaker(bracketOrPlain[1])
      if (mapped) {
        messages.push({
          speaker: mapped.speaker,
          text: (bracketOrPlain[2] || '').trim(),
          type: mapped.type
        })
        return
      }
    }

    // 3) Original explicit patterns (kept exactly as core behavior)
    const markedAgentMatch = line.match(/^\*\*Agent\*\*:\s*(.*)$/i)
    const markedCustomerMatch = line.match(/^\*\*Customer\*\*:\s*(.*)$/i)
    const plainAgentMatch = line.match(/^(Agent|Rep|Sales|Representative):\s*(.*)$/i)
    const plainCustomerMatch = line.match(/^(Customer|Client|Prospect|Caller):\s*(.*)$/i)

    if (markedAgentMatch) {
      messages.push({
        speaker: 'Agent',
        text: (markedAgentMatch[1] || '').trim(),
        type: 'agent'
      })
      return
    }
    if (markedCustomerMatch) {
      messages.push({
        speaker: 'Customer',
        text: (markedCustomerMatch[1] || '').trim(),
        type: 'customer'
      })
      return
    }
    if (plainAgentMatch) {
      messages.push({
        speaker: 'Agent',
        text: (plainAgentMatch[2] || '').trim(),
        type: 'agent'
      })
      return
    }
    if (plainCustomerMatch) {
      messages.push({
        speaker: 'Customer',
        text: (plainCustomerMatch[2] || '').trim(),
        type: 'customer'
      })
      return
    }

    // 4) No speaker detected — continuation of previous message (core behavior preserved)
    if (messages.length > 0) {
      // Append a space + the raw text (already normalized), preserving content
      messages[messages.length - 1].text += (messages[messages.length - 1].text ? ' ' : '') + line
    } else {
      // First line without speaker — assume Agent (core behavior preserved)
      messages.push({
        speaker: 'Agent',
        text: line,
        type: 'agent'
      })
    }
  })

  return messages
}

export const getRawTranscript = (transcript) => {
  // Keep original behavior and gently strip a bit more harmless formatting
  if (!transcript) return "No transcript available"

  return String(transcript)
    .replace(/\*\*/g, '')        // remove bold markers
    .replace(/##\s*/g, '')       // remove markdown headers
    .replace(/```[\s\S]*?```/g, '') // remove code fence blocks if present
    .trim()
}
